import React from 'react';
import { motion } from 'framer-motion';
import { FaFacebook, FaWhatsapp, FaInstagram, FaTelegram } from 'react-icons/fa';
import { Mail, Phone, MessageCircle } from 'lucide-react';

const channels = [
  { name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366', bg: 'bg-green-50 border-green-200' },
  { name: 'Instagram', icon: FaInstagram, color: '#E4405F', bg: 'bg-pink-50 border-pink-200' },
  { name: 'Facebook', icon: FaFacebook, color: '#1877F2', bg: 'bg-blue-50 border-blue-200' },
  { name: 'Telegram', icon: FaTelegram, color: '#0088CC', bg: 'bg-sky-50 border-sky-200' },
  { name: 'Email', icon: Mail, color: '#EA4335', bg: 'bg-red-50 border-red-200', isLucide: true },
  { name: 'Phone', icon: Phone, color: '#1a1a1a', bg: 'bg-slate-50 border-slate-200', isLucide: true },
  { name: 'Live Chat', icon: MessageCircle, color: '#0D9488', bg: 'bg-teal-50 border-teal-200', isLucide: true },
];

const MultiChannelSection = () => {
  return (
    <section className="py-16 sm:py-24 md:py-32 bg-white" data-testid="multichannel-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6">
            <MessageCircle className="w-4 h-4 text-teal-600" />
            <span className="text-xs sm:text-sm font-bold text-teal-700">Multi-Channel</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-5">
            One AI Agent.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-500">Every Channel.</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            Engage customers wherever they are. Arismate connects across all major communication platforms from a single dashboard.
          </p>
        </motion.div>

        {/* Channel Hub Visual */}
        <div className="relative max-w-4xl mx-auto">
          {/* Center Logo */}
          <motion.div
            className="flex justify-center mb-8 sm:mb-12"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="w-20 sm:w-28 h-20 sm:h-28 rounded-full flex items-center justify-center shadow-xl shadow-teal-600/30 relative p-3 bg-gradient-to-br from-teal-600 to-emerald-600">
              <img src="/assets/logo.png" alt="Arismate" className="brightness-0 invert w-full h-full object-contain" />
              {/* Pulse ring */}
              <div className="absolute inset-0 rounded-full border-2 border-teal-400 animate-ping opacity-75" />
            </div>
          </motion.div>

          {/* Channel Icons Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3 sm:gap-4">
            {channels.map((channel, index) => (
              <motion.div
                key={channel.name}
                className={`flex flex-col items-center gap-2 sm:gap-3 p-3 sm:p-5 rounded-2xl sm:rounded-3xl border-2 ${channel.bg} shadow-sm hover:shadow-lg transition-all duration-300 group`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                whileHover={{ y: -5 }}
                data-testid={`channel-${channel.name.toLowerCase().replace(' ', '-')}`}
              >
                <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-xl sm:rounded-2xl bg-white flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                  {channel.isLucide ? (
                    <channel.icon className="w-6 sm:w-8 h-6 sm:h-8" style={{ color: channel.color }} />
                  ) : (
                    <channel.icon className="w-6 sm:w-8 h-6 sm:h-8" style={{ color: channel.color }} />
                  )}
                </div>
                <span className="text-xs sm:text-sm font-bold text-slate-700 text-center">{channel.name}</span>
              </motion.div>
            ))}
          </div>

          {/* Connection Lines (visual only) */}
          <div className="hidden lg:block absolute top-28 left-1/2 transform -translate-x-1/2 w-[400px] h-[2px] bg-gradient-to-r from-transparent via-teal-200 to-transparent" />
        </div>

        {/* Stats */}
        <motion.div
          className="mt-12 sm:mt-16 grid grid-cols-3 gap-4 max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-extrabold text-teal-600">7+</p>
            <p className="text-xs sm:text-sm text-slate-500 font-medium">Channels</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-extrabold text-teal-600">1</p>
            <p className="text-xs sm:text-sm text-slate-500 font-medium">Dashboard</p>
          </div>
          <div className="text-center">
            <p className="text-2xl sm:text-3xl font-extrabold text-teal-600">24/7</p>
            <p className="text-xs sm:text-sm text-slate-500 font-medium">Active</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MultiChannelSection;
